import React from 'react';
import {animated, useSpring} from 'react-spring';
import {getTransitionDuration, TransitionRootProps} from './TransitionRoot';

export type FadeProps = TransitionRootProps;

const assignRef = <T,>(ref: React.Ref<T> | undefined, value: T | null) => {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    (ref as React.MutableRefObject<T | null>).current = value;
  }
};

export const Fade: React.FC<FadeProps> = ({
  children,
  timeout,
  in: inProp = false,
  mountOnEnter,
  unmountOnExit,
  onEnter,
  onEntered,
  onExit,
  onExited,
  nodeRef: nodeRefProp,
}) => {
  const child = React.Children.only(children) as React.ReactElement<any> | null;
  const localRef = React.useRef<HTMLElement | null>(null);
  const [shouldRender, setShouldRender] = React.useState(() => !mountOnEnter || inProp);

  React.useEffect(() => {
    if (inProp) {
      setShouldRender(true);
    } else if (!unmountOnExit) {
      setShouldRender(true);
    }
  }, [inProp, unmountOnExit]);

  const enterDuration = getTransitionDuration(timeout, 'enter');
  const exitDuration = getTransitionDuration(timeout, 'exit');

  const styles = useSpring({
    opacity: inProp ? 1 : 0,
    config: {duration: inProp ? enterDuration : exitDuration},
    onStart: () => {
      if (inProp) {
        onEnter?.(localRef.current);
      } else {
        onExit?.(localRef.current);
      }
    },
    onRest: () => {
      if (!inProp && unmountOnExit) {
        setShouldRender(false);
      }
      if (inProp) {
        onEntered?.(localRef.current);
      } else {
        onExited?.(localRef.current);
      }
    },
  });

  if (!child || !React.isValidElement(child) || !shouldRender) {
    return null;
  }

  const AnimatedComponent = animated(child.type as any);

  const handleRef = (node: HTMLElement | null) => {
    localRef.current = node;
    assignRef((child as any).ref, node);
    assignRef(nodeRefProp, node);
  };

  const childProps = child.props as any;

  return (
    <AnimatedComponent
      {...childProps}
      ref={handleRef}
      style={{
        ...childProps.style,
        opacity: styles.opacity,
        willChange: 'opacity',
      }}
    />
  );
};

export default Fade;
